import { BikeApi } from '@/apis';
import { Bike, BikeListItem, BikeModelItem } from '@/types/bike';
import { ResponseData } from '@/types/http';
import {
    AddRecommendationRequest,
    CreateBikeEkRequest,
    CreateBikeGasRequest,
    GetAllBikeRequest,
    GetAllBikeResponse,
    UnregisterLocationRequest,
    UpdateBikeEkRequest,
    UpdateBikeGasRequest
} from '@/types/http-payload/bike';
import { TableState } from '@tanstack/react-table';
import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';

export default class BikeStore extends BaseStore {
    bikeDetail: Bike | null = null;
    bikeList: BikeListItem[] = [];
    bikeModelList: BikeModelItem[] = [];
    api: BikeApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            bikeList: observable,
            bikeDetail: observable,
            bikeModelList: observable,
            getAllBike: flow.bound,
            registerLocation: flow.bound,
            unregisterLocation: flow.bound,
            createBikeEk: flow.bound,
            createBikeGas: flow.bound,
            getBikeDetail: flow.bound,
            updateBikeEk: flow.bound,
            updateBikeGas: flow.bound,
            createNonRentalDate: flow.bound,
            updateNonRentalDate: flow.bound,
            resetNonRentalDate: flow.bound,
            addRecommendation: flow.bound,
            deleteBike: flow.bound,
            reSendApproveBike: flow.bound,
            getListModelName: flow.bound,
            chekBikeHasOrderBeforeOff: flow.bound,
            getNonRentalDateSetting: flow.bound
        });
        this.api = new BikeApi();
    }

    // #region CMS
    *getAllBike(searchParams?: GetAllBikeRequest, paging?: TableState) {
        try {
            const payload = { ...this.convertPagingFromTableToRequest(paging ?? this.paging), ...searchParams };
            const res: ResponseData<GetAllBikeResponse> = yield this.rootStore.apiStore.call(this.api, this.api.getAllBike, payload);
            if (res.ok) {
                this.bikeList = res.data.elements;
                this.paging = {
                    ...this.paging,
                    sorting: paging?.sorting ?? [],
                    pagination: this.convertPaginationFromRequestToTable(res.data.paginate)
                };
                this.totalRecord = res.data.paginate.totalRecord;
            }
        } catch (error) {
        }
    }

    *createBikeEk(payload: CreateBikeEkRequest) {
        try {
            const res: ResponseData<Bike> = yield this.rootStore.apiStore.call(this.api, this.api.createBikeEk, payload);
            if (res?.ok) {
                this.bikeDetail = res.data;
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *createBikeGas(payload: CreateBikeGasRequest) {
        try {
            const res: ResponseData<Bike> = yield this.rootStore.apiStore.call(this.api, this.api.createBikeGas, payload);
            if (res?.ok) {
                this.bikeDetail = res.data;
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *chekBikeHasOrderBeforeOff(payload) {
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(this.api, this.api.checkBikeHasBookingBeforeOff, payload);
            if (res?.ok) {
                return res.data;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *updateBikeEk(payload: UpdateBikeEkRequest) {
        try {
            const res: ResponseData<Bike> = yield this.rootStore.apiStore.call(this.api, this.api.updateBikeEk, payload);
            if (res?.ok) {
                this.bikeDetail = res.data;
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *updateBikeGas(payload: UpdateBikeGasRequest) {
        try {
            const res: ResponseData<Bike> = yield this.rootStore.apiStore.call(this.api, this.api.updateBikeGas, payload);
            if (res?.ok) {
                this.bikeDetail = res.data;
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *deleteBike(id: number) {
        try {
            const res: ResponseData<Bike> = yield this.rootStore.apiStore.call(this.api, this.api.deleteBike, id);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *getBikeDetail(id: number) {
        try {
            const res: ResponseData<Bike> = yield this.rootStore.apiStore.call(this.api, this.api.getBikeDetail, id);
            if (res?.ok) {
                this.bikeDetail = res?.data || null;
                return res?.data;
            }
            return false;
        } catch (error) {
            return false;
        }
    };

    *getNonRentalDateSetting(payload: any) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getNonRentalDateSetting, payload);
            if (res?.ok) {
                return res?.data;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *registerLocation(payload: UnregisterLocationRequest) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.registerLocation, payload);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *unregisterLocation(payload: UnregisterLocationRequest) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.unregisterLocation, payload);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *createNonRentalDate(payload: any) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.createNonRentalDate, payload);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *updateNonRentalDate(payload: any) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.updateNonRentalDate, payload);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *resetNonRentalDate(payload: any) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.resetNonRentalDate, payload);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *addRecommendation(payload: AddRecommendationRequest) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.addRecommendation, payload);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *reSendApproveBike(id: number) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.reSendApproveBike, id);
            if (res?.ok) {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }

    *getListModelName() {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(this.api, this.api.getListModelName);
            if (res?.ok) {
                this.bikeModelList = res.data;
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    }
    // #endregion

    clean(): void {
        super.clean();

        this.bikeList = [];
        this.bikeDetail = null;
    }
}
